import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';

export interface UseDateiDropOptions {
  /** Wie das accept-Attribut von <input type="file">, z. B. "image/*,.csv" */
  accept?: string;
  /** Maximale Dateigröße in Bytes */
  maxBytes?: number;
  /** Erlaubt mehrere Dateien pro Drop (Standard: false → nur die erste Datei wird verwendet) */
  multiple?: boolean;
  /** Aufgerufen mit den gültigen Dateien nach Accept-/Größenprüfung */
  onFiles: (dateien: File[]) => void;
  /** Aufgerufen bei einer ungültigen Datei (deutscher Fehlertext, eine Meldung pro Datei) */
  onError?: (fehler: string) => void;
}

export interface UseDateiDropResult {
  dragOver: boolean;
  dropHandlers: {
    onDragEnter: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Prüft eine Datei gegen ein accept-Muster (MIME wie "image/*" oder Endung wie ".csv"). */
function passtZuAccept(datei: File, accept: string): boolean {
  const muster = accept
    .split(',')
    .map(m => m.trim().toLowerCase())
    .filter(Boolean);
  if (muster.length === 0) return true;

  const name = datei.name.toLowerCase();
  const typ = (datei.type || '').toLowerCase();

  return muster.some(m => {
    if (m.startsWith('.')) return name.endsWith(m);
    if (m.endsWith('/*')) return typ.startsWith(m.slice(0, -1));
    return typ === m;
  });
}

/**
 * Gemeinsamer Drag-and-Drop-Hook für Datei-Uploads.
 *
 * Löst das Drag-Leave-Flackern bei verschachtelten Kind-Elementen über einen
 * Enter/Leave-Zähler (statt eines einfachen Booleans), prüft optional
 * accept-Muster und Dateigröße und meldet Fehler als deutschen Text über
 * onError statt sie stillschweigend zu verwerfen.
 *
 * Verwendung: `<div {...dropHandlers} className={dragOver ? 'is-drag-over' : ''}>`
 */
export function useDateiDrop(options: UseDateiDropOptions): UseDateiDropResult {
  const { accept, maxBytes, multiple = false, onFiles, onError } = options;
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);

  const validiereUndMelde = useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const alle = Array.from(fileList);
    const kandidaten = multiple ? alle : alle.slice(0, 1);

    const gueltig: File[] = [];
    for (const datei of kandidaten) {
      if (accept && !passtZuAccept(datei, accept)) {
        onError?.(`Dateityp nicht erlaubt: ${datei.name}`);
        continue;
      }
      if (maxBytes != null && datei.size > maxBytes) {
        onError?.(`Datei zu groß: ${datei.name} (max. ${formatBytes(maxBytes)})`);
        continue;
      }
      gueltig.push(datei);
    }
    if (gueltig.length > 0) onFiles(gueltig);
  }, [accept, maxBytes, multiple, onFiles, onError]);

  const onDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer?.types?.includes('Files')) return;
    dragCounter.current += 1;
    setDragOver(true);
  }, []);

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setDragOver(false);
  }, []);

  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    validiereUndMelde(e.dataTransfer?.files ?? null);
  }, [validiereUndMelde]);

  return {
    dragOver,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
